'use client';

import { useState, useMemo, useEffect } from 'react';
import debounce from 'lodash-es/debounce';
import { usePageEditorStore } from 'app/store/editor';
import { FEATURE_FLAG_KEYS, CSSVariablesMap } from 'shop-ui/constants';
import { getHeaderHeight, getFooterHeight } from 'shop-ui/tools/utils';

export function useSiteStyle() {
    const [headerHeight, setHeaderHeight] = useState(getHeaderHeight());

    const [footerHeight, setFooterHeight] = useState(getFooterHeight());

    const _siteStyle = usePageEditorStore((api) => api.siteStyle);

    const siteStyle = useMemo(() => {
        if (!_siteStyle) {
            return {
                useHeader: false,
            };
        }

        let siteWidth = '';

        switch (_siteStyle?.[FEATURE_FLAG_KEYS.SITE_WIDTH]) {
            case 'custom':
                siteWidth = _siteStyle[FEATURE_FLAG_KEYS.SITE_WIDTH_CUSTOM] || '';

                break;

            case 'large':
                siteWidth = '1200px';

                break;

            case 'medium':
                siteWidth = '1000px';

                break;

            case 'small':
                siteWidth = '768px';

                break;
        }

        const siteBackgroundColor = _siteStyle?.[FEATURE_FLAG_KEYS.SITE_BACKGROUND_COLOR];

        return {
            useHeader: _siteStyle?.[FEATURE_FLAG_KEYS.SITE_PAGE_HEADER_ENABLED],
            ...(siteWidth && {
                [CSSVariablesMap[FEATURE_FLAG_KEYS.SITE_WIDTH]]: siteWidth,
            }),
            ...(siteBackgroundColor && {
                [CSSVariablesMap[FEATURE_FLAG_KEYS.SITE_BACKGROUND_COLOR]]:
                    siteBackgroundColor,
            }),
        };
    }, [_siteStyle]);

    const topLevelFontStyle = useMemo(() => {
        if (!_siteStyle) {
            return {
                useHeader: false,
            };
        }

        const topLevelFontProperties = [
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_FONT_FAMILY,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_HEADING_FONT_SIZE,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_HEADING_FONT_SIZE,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_HEADING_FONT_SIZE,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_PARAGRAPH_FONT_SIZE,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_PARAGRAPH_FONT_SIZE,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_PARAGRAPH_FONT_SIZE,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_HEADING_FONT_WEIGHT,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_HEADING_FONT_WEIGHT,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_HEADING_FONT_WEIGHT,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_PARAGRAPH_FONT_WEIGHT,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_PARAGRAPH_FONT_WEIGHT,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_PARAGRAPH_FONT_WEIGHT,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_HEADING_LETTER_SPACING,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_HEADING_LETTER_SPACING,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_HEADING_LETTER_SPACING,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_PARAGRAPH_LETTER_SPACING,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_PARAGRAPH_LETTER_SPACING,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_PARAGRAPH_LETTER_SPACING,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_HEADING_COLOR,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_HEADING_COLOR,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_HEADING_COLOR,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_PARAGRAPH_COLOR,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_PARAGRAPH_COLOR,
            FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_PARAGRAPH_COLOR,
        ];

        return topLevelFontProperties.reduce((acc, property) => {
            if (
                (property === FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_HEADING_FONT_SIZE ||
                    property ===
                        FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_HEADING_FONT_SIZE ||
                    property ===
                        FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_HEADING_FONT_SIZE ||
                    property ===
                        FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_LARGE_PARAGRAPH_FONT_SIZE ||
                    property ===
                        FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_MEDIUM_PARAGRAPH_FONT_SIZE ||
                    property ===
                        FEATURE_FLAG_KEYS.SITE_TOP_LEVEL_SMALL_PARAGRAPH_FONT_SIZE) &&
                _siteStyle?.[property]
            ) {
                acc[CSSVariablesMap[property]] = _siteStyle?.[property] + 'px';
            } else {
                acc[CSSVariablesMap[property]] = _siteStyle?.[property];
            }

            return acc;
        }, {});
    }, [_siteStyle]);

    useEffect(() => {
        const config = { childList: true, subtree: true };

        const headerMutationCallback = () => {
            setHeaderHeight(getHeaderHeight());
        };

        const headerResizeCallback = () => {
            setHeaderHeight(getHeaderHeight());
        };

        const footerCallback = () => {
            setFooterHeight(getFooterHeight());
        };

        const headerNode = document.querySelector('header');

        const headerMutationObserver = new MutationObserver(
            debounce(headerMutationCallback, 100)
        );

        const headerResizeObserver = new ResizeObserver(
            debounce(headerResizeCallback, 100)
        );

        if (headerNode) {
            headerMutationObserver.observe(headerNode, config);
            headerResizeObserver.observe(headerNode);
        }

        const resizeObserver = new ResizeObserver(debounce(footerCallback, 100));

        const footerNode = document.querySelector('footer');

        if (footerNode) {
            resizeObserver.observe(footerNode);
        }

        setHeaderHeight(getHeaderHeight());
        setFooterHeight(getFooterHeight());

        return () => {
            headerMutationObserver.disconnect();
            resizeObserver.disconnect();
        };
    }, []);

    return {
        siteStyle,
        topLevelFontStyle,
        headerHeight,
        footerHeight,
    };
}
